import { Resources } from 'constants/resources';

import { Children, isValidElement, useCallback } from 'react';
import * as React from 'react';

import clsx from 'clsx';
import { usePermissions } from 'hooks/usePermissions';
import { DatagridHeaderCell, useListContext } from 'react-admin';
import { Checkbox } from 'shared/mui/Checkbox/Checkbox';
import { TableCell, TableHead, TableRow } from 'shared/mui/Table';
import { BankOperation } from 'types/embedded/bankOperation';
import { Permission } from 'types/embedded/permission';

type Props = {
  children?: React.ReactNode;
  className?: string;
  hasExpand?: boolean;
  hasBulkActions?: boolean;
  isRowSelectable?: (record: BankOperation) => boolean;
};

export const RequestsBankOpsListDatagridHeader = (props: Props) => {
  const { children, className, hasBulkActions = false, isRowSelectable } = props;
  const { sort, data, onSelect, selectedIds, setSort } = useListContext();
  const { hasResourcePermissions } = usePermissions();

  const updateSortCallback = useCallback(
    (event: any) => {
      event.stopPropagation();
      const newField = event.currentTarget.dataset.field;
      const newOrder =
        sort.field === newField
          ? sort.order === 'ASC'
            ? 'DESC'
            : 'ASC'
          : event.currentTarget.dataset.order;

      setSort({ field: newField, order: newOrder });
    },
    [sort.field, sort.order, setSort],
  );

  const handleSelectAll = useCallback(
    (event: any) =>
      onSelect(
        data && event.target.checked
          ? selectedIds.concat(
              data
                .filter((record) => !selectedIds.includes(record.id))
                .filter((record) => (isRowSelectable ? isRowSelectable(record) : true))
                .map((record) => record.id),
            )
          : [],
      ),
    [data, onSelect, isRowSelectable, selectedIds],
  );

  const selectableIds = Array.isArray(data)
    ? isRowSelectable
      ? data.filter((record) => isRowSelectable(record)).map((record) => record.id)
      : data.map((record) => record.id)
    : [];

  return (
    <TableHead className={clsx(className, 'RaDatagrid-thead')}>
      <TableRow className={clsx('RaDatagrid-row', 'RaDatagrid-headerRow')}>
        {hasBulkActions && selectedIds && (
          <TableCell
            className="RaDatagrid-headerCell"
            padding="checkbox"
            sx={{ padding: '13px 16px!important' }}
          >
            <Checkbox
              checked={
                selectedIds.length > 0 &&
                selectableIds.length > 0 &&
                selectableIds.every((id) => selectedIds.includes(id))
              }
              className="select_all"
              color="primary"
              disabled={
                !hasResourcePermissions(Resources.RequestBankOperations, [
                  Permission.Delete,
                  Permission.Full,
                ])
              }
              onChange={handleSelectAll}
              onClick={(e) => e.stopPropagation()}
              sx={{ padding: 0 }}
            />
          </TableCell>
        )}
        {Children.map(children, (field, index) =>
          isValidElement(field) ? (
            <DatagridHeaderCell
              className={clsx('RaDatagrid-headerCell', `column-${(field.props as any).source}`)}
              field={field}
              isSorting={
                sort.field === ((field.props as any).sortBy || (field.props as any).source)
              }
              key={(field.props as any).source || index}
              sort={sort}
              updateSort={updateSortCallback}
            />
          ) : null,
        )}
      </TableRow>
    </TableHead>
  );
};
